import classNames from "classnames";
import { useQuery } from "@apollo/client";
import { useState } from "react";
import { DateTime } from "luxon";
import EmptyState from "@/components/EmptyState";
import MetricCard from "@/components/MetricCard/MetricCard";
import { RowHeader, RowLink, TableCard, TableCardRow } from "@/components/Table";
import Tag from "@/components/Tag";
import useCapabilities, { useCanViewCareerServicesDashboardBool } from "@/hooks/useCapabilities";
import { useSearchParams } from "react-router-dom";
import { type Path, useNavigate } from "@/router";
import {
  JOB_DASHBOARD_QUEUES_QUERY,
  type JobDashboardQueue,
  type JobDashboardQueueRow,
  type JobDashboardQueuesQueryVariables
} from "../queries";
import CareerServicesDashboardGate from "./CareerServicesDashboardGate";
import DashboardEmptyRow from "./DashboardEmptyRow";
import DashboardRowDetails from "./DashboardRowDetails";
import DashboardTimeframeToggle from "./DashboardTimeframeToggle";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";
import {
  DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS,
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL
} from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";
import { dashboardWindowLabel, DEFAULT_DASHBOARD_WINDOW } from "../_utils/dashboardTimeframe";

const COLUMN_COUNT = 3;
const JOB_DASHBOARD_PREVIEW_LIMIT = 3;
const STATUS_CELL = "w-32 min-w-0 whitespace-nowrap align-middle py-3 text-left";
const ACTIVITY_CELL =
  "w-32 min-w-0 whitespace-normal break-words align-middle py-3 text-left text-body-medium text-sys-brand-on-surface-variant";
const JOB_ROW_HEADER_CLASS = "[&>td>div>span]:min-w-0 [&>td>div>span]:shrink";

type JobQueueKind = "drafts" | "reviews" | "expired";
type JobStatusTagType = "warn" | "information" | "error";
type JobQueueConfig = {
  label: (windowLabel: string) => string;
  emptyLabel: string;
  statusLabel: string;
  statusTagType: JobStatusTagType;
  activityPrefix: string;
};

type DashboardJobPosting = {
  id: string;
  title: string;
  facility: string;
  activityLabel: string;
  statusLabel: string;
  statusTagType: JobStatusTagType;
  href: Path;
};

type DashboardJobPostingGroup = {
  id: JobQueueKind;
  label: string;
  emptyLabel: string;
  totalCount: number;
  rows: DashboardJobPosting[];
};

const JOB_QUEUE_CONFIG: Record<JobQueueKind, JobQueueConfig> = {
  drafts: {
    label: (windowLabel) => `STARTED BY YOU · DRAFTS TO FINISH · ${windowLabel}`,
    emptyLabel: "No drafts to finish.",
    statusLabel: "Draft",
    statusTagType: "warn",
    activityPrefix: "Started"
  },
  reviews: {
    label: (windowLabel) => `ASSIGNED TO YOU · NEEDS REVIEW · ${windowLabel}`,
    emptyLabel: "No job postings need review.",
    statusLabel: "Needs review",
    statusTagType: "information",
    activityPrefix: "Sent"
  },
  expired: {
    label: () => "ASSIGNED TO YOU · EXPIRED · CLOSE NEEDED",
    emptyLabel: "No expired job postings.",
    statusLabel: "Expired",
    statusTagType: "error",
    activityPrefix: "Expired"
  }
};

const JOB_METRIC_CONFIG = [
  { id: "live", label: () => "Live", iconName: "work", variant: "success" as const },
  {
    id: "created",
    label: (windowLabel: string) => `Created · ${windowLabel}`,
    iconName: "publish",
    variant: "success" as const
  },
  {
    id: "closed",
    label: (windowLabel: string) => `Closed · ${windowLabel}`,
    iconName: "task_alt",
    variant: "info" as const
  }
] as const;

function formatDaysAgo(value: string | null): string {
  if (!value) {
    return "recently";
  }

  const date = DateTime.fromISO(value);
  if (!date.isValid) {
    return "recently";
  }

  const daysAgo = Math.max(0, Math.floor(DateTime.now().startOf("day").diff(date.startOf("day"), "days").days));
  if (daysAgo === 0) {
    return "today";
  }

  return `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;
}

function jobPostingHref(queue: JobQueueKind, id: string): Path {
  if (queue === "drafts") {
    return `/jobs/job-post/${id}/details` as Path;
  }

  if (queue === "reviews") {
    return `/jobs/job-post/${id}/reviews` as Path;
  }

  return `/jobs/job-post/close/${id}` as Path;
}

function toDashboardJobPosting(row: JobDashboardQueueRow, queue: JobQueueKind): DashboardJobPosting {
  const config = JOB_QUEUE_CONFIG[queue];
  const activityDate = queue === "expired" ? row.submissionDeadline : row.createdAt;
  const details = [row.facilityName, row.sectionName].filter(Boolean).join(" · ");

  return {
    id: row.id,
    title: row.name ?? "Untitled job posting",
    facility: details || "Facility unavailable",
    activityLabel: `${config.activityPrefix} ${formatDaysAgo(activityDate)}`,
    statusLabel: config.statusLabel,
    statusTagType: config.statusTagType,
    href: jobPostingHref(queue, row.id)
  };
}

function toDashboardJobPostingGroup(
  queue: JobDashboardQueue,
  kind: JobQueueKind,
  windowLabel: string
): DashboardJobPostingGroup {
  const config = JOB_QUEUE_CONFIG[kind];

  return {
    id: kind,
    label: config.label(windowLabel),
    emptyLabel: config.emptyLabel,
    totalCount: queue.totalCount,
    rows: queue.rows.map((row) => toDashboardJobPosting(row, kind))
  };
}

function JobPostingStatus({ job }: { job: DashboardJobPosting }) {
  return (
    <Tag tagType={job.statusTagType} tagStyle="standard" size="small">
      {job.statusLabel}
    </Tag>
  );
}

function JobPostingRow({ job }: { job: DashboardJobPosting }) {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const jobDetails = job.facility;
  const openJob = () => navigate(job.href as Path);

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`home-job-row-${job.id}`}
      onClick={openJob}
      ariaLabel={`Open job posting: ${job.title}`}
      stackedLayout={isStackedTableRows}
      mobile={
        <div className="flex min-w-0 flex-col gap-2">
          <DashboardRowDetails icon="work" title={job.title} text={jobDetails} />
          <div className="flex flex-wrap items-center justify-between gap-2 pl-14">
            <JobPostingStatus job={job} />
            <span className="text-body-small text-sys-brand-on-surface-variant">{job.activityLabel}</span>
          </div>
        </div>
      }
    >
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "max-w-0")}>
        <DashboardRowDetails icon="work" title={job.title} text={jobDetails} />
      </td>
      <td className={STATUS_CELL}>
        <JobPostingStatus job={job} />
      </td>
      <td className={ACTIVITY_CELL}>{job.activityLabel}</td>
    </TableCardRow>
  );
}

function JobPostingGroup({ group }: { group: DashboardJobPostingGroup }) {
  return (
    <>
      <RowHeader
        label={group.label}
        count={group.totalCount}
        columnCount={COLUMN_COUNT}
        rowTestId={`home-jobs-group-${group.id}`}
        className={JOB_ROW_HEADER_CLASS}
      />
      {group.rows.length > 0 ? (
        group.rows.map((job) => <JobPostingRow key={job.id} job={job} />)
      ) : (
        <DashboardEmptyRow
          columnCount={COLUMN_COUNT}
          message={group.emptyLabel}
          rowTestId={`home-jobs-group-${group.id}-empty`}
        />
      )}
    </>
  );
}

function JobsDashboardContent() {
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const [searchParams] = useSearchParams();
  const [dashboardWindow, setDashboardWindow] = useState(DEFAULT_DASHBOARD_WINDOW);
  const { loaded: capabilitiesLoaded } = useCapabilities();
  const canViewCareerServicesDashboard = useCanViewCareerServicesDashboardBool();
  const sectionId = searchParams.get("sectionId");
  const windowLabel = dashboardWindowLabel(dashboardWindow);
  const variables: JobDashboardQueuesQueryVariables = {
    input: {
      window: dashboardWindow,
      previewLimit: JOB_DASHBOARD_PREVIEW_LIMIT,
      ...(sectionId ? { sectionId } : {})
    }
  };
  const { data, previousData, loading, networkStatus, error } = useQuery(JOB_DASHBOARD_QUEUES_QUERY, {
    variables,
    skip: !capabilitiesLoaded || !canViewCareerServicesDashboard,
    fetchPolicy: "cache-and-network"
  });
  const dashboardQueues = data?.jobDashboardQueues ?? previousData?.jobDashboardQueues;
  const jobGroups = dashboardQueues
    ? [
        toDashboardJobPostingGroup(dashboardQueues.drafts, "drafts", windowLabel.toUpperCase()),
        toDashboardJobPostingGroup(dashboardQueues.reviews, "reviews", windowLabel.toUpperCase()),
        toDashboardJobPostingGroup(dashboardQueues.expired, "expired", windowLabel.toUpperCase())
      ]
    : [];
  const totalJobRows = jobGroups.reduce((total, group) => total + group.rows.length, 0);
  const dashboardLoading =
    !capabilitiesLoaded ||
    (canViewCareerServicesDashboard &&
      isInitialQueryLoading({
        loading,
        networkStatus,
        itemCount: totalJobRows,
        hasData: dashboardQueues !== undefined,
        error
      }));
  const dashboardUnavailable = capabilitiesLoaded && !canViewCareerServicesDashboard;
  const dashboardError = Boolean(error && !dashboardQueues);
  const metricCounts = new Map(
    (dashboardQueues?.metrics ?? []).map((metric) => [metric.key.toLowerCase(), metric.totalCount])
  );

  return (
    <div
      className={classNames("mt-12 flex flex-col gap-6", DASHBOARD_BOTTOM_SCROLL_PADDING_CLASS)}
      data-testid="home-jobs-dashboard"
    >
      <div className="flex w-full justify-start" data-testid="home-jobs-timeframe-row">
        <DashboardTimeframeToggle value={dashboardWindow} onChange={setDashboardWindow} testId="home-jobs-timeframe" />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          <TableCard
            title="Job Postings"
            icon="work"
            testId="home-jobs-needs-attention"
            shellClassName={DASHBOARD_PANEL_SHELL_CLASS}
            bodyClassName={classNames(DASHBOARD_TABLE_CARD_BODY_CLASS, "overflow-x-auto")}
          >
            <table
              className={getDashboardPreviewTableClassName(isStackedTableRows)}
              data-testid="home-jobs-triage-table"
              aria-label="Job posting priorities"
            >
              {isStackedTableRows ? null : (
                <colgroup>
                  <col />
                  <col className="w-32" />
                  <col className="w-32" />
                </colgroup>
              )}
              <tbody>
                {dashboardLoading ? (
                  <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
                ) : dashboardUnavailable ? (
                  <tr>
                    <td colSpan={COLUMN_COUNT}>
                      <EmptyState
                        iconName="lock"
                        title="Career Services Dashboard Unavailable"
                        caption="You do not have permission to view Career Services job activity."
                      />
                    </td>
                  </tr>
                ) : dashboardError ? (
                  <tr data-testid="home-jobs-error-row">
                    <td colSpan={COLUMN_COUNT}>
                      <EmptyState
                        iconName="error"
                        title="Unable to Load Job Postings"
                        caption="Try refreshing the page. If the problem continues, contact support."
                      />
                    </td>
                  </tr>
                ) : (
                  jobGroups.map((group) => <JobPostingGroup key={group.id} group={group} />)
                )}
                {!dashboardLoading && !dashboardUnavailable && !dashboardError ? (
                  <RowLink columnCount={COLUMN_COUNT} to="/jobs" testId="home-jobs-view-all">
                    View all job postings
                  </RowLink>
                ) : null}
              </tbody>
            </table>
          </TableCard>
        </div>

        <aside className="flex w-full flex-col gap-4" data-testid="home-jobs-metrics">
          {JOB_METRIC_CONFIG.map((metric) => (
            <MetricCard
              key={metric.id}
              iconName={metric.iconName}
              label={metric.label(windowLabel)}
              value={metricCounts.has(metric.id) ? String(metricCounts.get(metric.id)) : null}
              loading={dashboardLoading}
              variant={metric.variant}
              testId={`home-jobs-metric-${metric.id}`}
            />
          ))}
        </aside>
      </div>
    </div>
  );
}

export default function JobsDashboard() {
  return (
    <CareerServicesDashboardGate>
      <JobsDashboardContent />
    </CareerServicesDashboardGate>
  );
}
